import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import React from "react"
import { useTranslation } from "react-i18next"

import { RowSubmitState } from "./useGridSubmit"

import "./SubmissionGrid.scss"

export type GridRow<TRow> = {
  key: string
  row: TRow
}

type Props<TRow> = {
  rows: GridRow<TRow>[]
  columns: ColumnDef<GridRow<TRow>, any>[]
  states: Record<string, RowSubmitState>
  onAdd: () => void
  onRemove: (key: string) => void
  onSubmit: () => void
  isSubmitting?: boolean
  isReadOnly?: boolean
  saveLabel?: string
  hideFooterSave?: boolean
}

function statusIndicator(
  state: RowSubmitState | undefined,
  t: any,
): JSX.Element | null {
  if (!state) return null
  switch (state.status) {
    case "saving":
      return (
        <span
          className="srs-grid-status srs-grid-status--saving"
          title={t("Saving…")}
        >
          ⟳
        </span>
      )
    case "saved":
      return (
        <span
          className="srs-grid-status srs-grid-status--saved"
          title={t("Saved")}
        >
          ✓
        </span>
      )
    case "error":
      return (
        <span
          className="srs-grid-status srs-grid-status--error"
          title={state.errors.join("; ")}
        >
          !
        </span>
      )
    default:
      return null
  }
}

function SubmissionGridInner<TRow>({
  rows,
  columns,
  states,
  onAdd,
  onRemove,
  onSubmit,
  isSubmitting = false,
  isReadOnly = false,
  saveLabel,
  hideFooterSave = false,
}: Props<TRow>) {
  const { t } = useTranslation()

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const renderedRows = table.getRowModel().rows

  return (
    <div
      className={`srs-grid-wrapper ${
        isReadOnly ? "srs-grid-wrapper--readonly" : ""
      }`}
    >
      <table className="srs-grid">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </th>
              ))}
              <th className="srs-grid-col-status" />
              <th className="srs-grid-col-actions" />
            </tr>
          ))}
        </thead>
        <tbody>
          {renderedRows.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} className="srs-grid-empty">
                {t("No rows yet — click + to add one.")}
              </td>
              <td className="srs-grid-col-actions">
                {!isReadOnly && (
                  <button
                    type="button"
                    className="srs-grid-btn srs-grid-btn--add"
                    onClick={onAdd}
                    title={t("Add row")}
                  >
                    +
                  </button>
                )}
              </td>
            </tr>
          ) : (
            renderedRows.map((tableRow, index) => {
              const key = tableRow.original.key
              const state = states[key]
              const isLast = index === renderedRows.length - 1
              return (
                <React.Fragment key={tableRow.id}>
                  <tr
                    className={
                      state?.status === "error" ? "srs-grid-row--error" : ""
                    }
                  >
                    {tableRow.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                    <td className="srs-grid-col-status">
                      {statusIndicator(state, t)}
                    </td>
                    <td className="srs-grid-col-actions">
                      {!isReadOnly && (
                        <div className="srs-grid-row-actions">
                          {isLast && (
                            <button
                              type="button"
                              className="srs-grid-btn srs-grid-btn--add"
                              onClick={onAdd}
                              title={t("Add row")}
                            >
                              +
                            </button>
                          )}
                          <button
                            type="button"
                            className="srs-grid-btn srs-grid-btn--remove"
                            onClick={() => onRemove(key)}
                            title={t("Remove row")}
                          >
                            −
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {state?.status === "error" && state.errors.length > 0 && (
                    <tr className="srs-grid-row--error-detail">
                      <td colSpan={columns.length + 2}>
                        <strong>{t("Cannot save:")}</strong>{" "}
                        {state.errors.join("; ")}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })
          )}
        </tbody>
      </table>
      {!isReadOnly && !hideFooterSave && (
        <div className="srs-grid-footer">
          <button
            type="button"
            className="btn btn-primary srs-grid-save"
            onClick={onSubmit}
            disabled={isSubmitting || renderedRows.length === 0}
          >
            {isSubmitting ? t("Saving…") : saveLabel ?? t("Save")}
          </button>
        </div>
      )}
    </div>
  )
}

const SubmissionGrid = SubmissionGridInner as <TRow>(
  props: Props<TRow>,
) => JSX.Element

export default SubmissionGrid
